import React, { ReactNode } from 'react'
import styled, { css } from 'styled-components'

import { themeEstra } from 'src/styles/themeEstra'

export interface InputProps {
  id?: string
  value: string
  onChange: (value: string | React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: () => void
  onConfirm?: () => void
  placeholder?: string
  inputSize?: 'large' | 'normal'
  rounded?: boolean
  shadow?: boolean
  invalid?: boolean
  type?:
    | 'password'
    | 'email'
    | 'text'
    | 'tel'
    | 'url'
    | 'number'
    | 'textarea'
    | 'select'
  className?: string
  name?: string
  block?: boolean
  selectCaretSize?: SelectCaretSize
  textPrefix?: string
  sideButton?: ReactNode
  size?: number
  bgColor?: string

  readOnly?: boolean

  disabled?: boolean

  maxLenght?: number

  isInDatePicker?: boolean

  children?: ReactNode
}

type SelectCaretSize = 'small' | 'large'
const SELECT_CARET: Record<SelectCaretSize, string> = {
  small:
    "data:image/svg+xml,%3Csvg width='10' height='6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M5 5.7L0 .718.72 0 5.01 4.297 9.291.012 10 .718 5 5.7z' fill='%23A5AAB2'/%3E%3C/svg%3E",
  large:
    "data:image/svg+xml,%3Csvg width='20' height='16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs/%3E%3Cpath d='M19.555 9.177V1.83a1 1 0 00-1.54-.841l-7.697 4.94a1 1 0 01-1.08 0L1.54.99A1 1 0 000 1.83v7.347a1 1 0 00.46.842l8.778 5.634a1 1 0 001.08 0l8.778-5.634a1 1 0 00.46-.842z' fill='%230E1A2F'/%3E%3C/svg%3E",
}

export const Input = React.forwardRef<
  () => HTMLInputElement | undefined,
  InputProps
>(
  (
    {
      id,
      value,
      onChange,
      placeholder,
      onBlur,
      onConfirm,
      rounded = true,
      inputSize = 'normal',
      shadow = false,
      type,
      children,
      invalid,
      className,
      name,
      block,
      selectCaretSize,
      textPrefix,
      sideButton,
      size,
      bgColor,
      readOnly,
      disabled,
      maxLenght,
      isInDatePicker,
      ...rest
    },
    ref
  ) => {
    return (
      <Wrapper
        isSelect={type === 'select'}
        block={block}
        hasSideButton={Boolean(sideButton)}
        inputRounded={rounded}
        className={className}
        caretSize={selectCaretSize}
      >
        {textPrefix && <Prefix rounded={rounded}>{textPrefix}</Prefix>}
        <Element
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          as={
            (type === 'select'
              ? 'select'
              : type === 'textarea'
              ? 'textarea'
              : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                undefined) as any
          }
          id={id}
          name={name}
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.currentTarget.value

            // If in a datepicker is needed to pass back the event not the value
            if (isInDatePicker) {
              onChange(e)
              return
            }

            let valueToPass = size && value ? value.slice(0, size) : value
            if (type === 'tel' || type === 'number') {
              valueToPass = valueToPass.replace(/\D/g, '')
            }
            onChange(valueToPass)
          }}
          onBlur={onBlur}
          onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && onConfirm) {
              onConfirm()
            }
          }}
          placeholder={placeholder}
          rounded={rounded}
          hasPrefix={Boolean(textPrefix)}
          hasSideButton={Boolean(sideButton)}
          inputSize={inputSize}
          shadow={shadow}
          type={type === 'select' ? undefined : type}
          invalid={invalid}
          bgColor={bgColor}
          readOnly={readOnly}
          disabled={disabled}
          maxLength={maxLenght}
          {...rest}
          ref={ref}
        >
          {type === 'select' && children ? children : null}
        </Element>
        {sideButton}
      </Wrapper>
    )
  }
)

Input.displayName = 'Input' // Typescript workaround for ForwardRef Component

const focusShadow = `inset 0px 0px 3px ${themeEstra.link.color}`
const errorShadow = `inset 0px 0px 3px ${themeEstra.color.primari.arancione}`
const inputPrefixSize = '50px'
const inputSideButtonSize = '50px'

interface WrapperProps {
  isSelect?: boolean
  caretSize?: SelectCaretSize
  block?: boolean

  inputRounded?: boolean
  hasSideButton?: boolean
}

const Wrapper = styled.div<WrapperProps>`
  position: relative;
  width: 100%;

  ${({ hasSideButton }) =>
    hasSideButton
      ? css`
          button {
            position: absolute;
            top: 0px;
            bottom: 0px;
            right: 0px;
            border-radius: ${inputRounded =>
              inputRounded ? '0px 20px 20px 0px' : '0'};
            width: ${inputSideButtonSize};
            display: flex;
            align-items: center;
            text-align: center;
            justify-content: center;
            margin: 0.2rem;
            height: auto;
          }

          img {
            margin-right: 0;
          }
        `
      : ''}

  ${({ block }) =>
    block
      ? css`
          display: block;
          width: 100%;
        `
      : css`
          display: inline-block;
        `}

  ${({ isSelect, caretSize }) =>
    isSelect
      ? css`
          position: relative;
          cursor: pointer;
          &:after {
            content: "";
            background-image: url("${SELECT_CARET[caretSize || 'large']}");
            background-position: center;
            background-repeat: no-repeat;
            position: absolute;
            top: 50%;
            right: 1rem;
            width: 20px;
            height: 16px;
            transform: translateY(-50%);
            pointer-events: none;
            touch-action: none;
          }
        `
      : null}
`

interface ElementProps {
  inputSize: 'large' | 'normal'
  rounded: boolean
  shadow: boolean
  invalid: boolean
  hasPrefix?: boolean
  hasSideButton?: boolean
  bgColor?: string
}

const getShadowStyle = ({ shadow }: ElementProps) =>
  shadow
    ? css`
        box-shadow: ${({ theme }) => theme.shadow.espansa};
        border: 0;
        &:focus,
        &:active {
          box-shadow: ${({ theme }) => theme.shadow.espansa}, ${focusShadow};
        }
      `
    : css`
        border: 1px solid ${({ theme }) => theme.color.neutri.grigioChiaro};

        &:focus,
        &:active {
          box-shadow: ${focusShadow};
        }
      `

const getErrorStyle = ({ shadow, invalid }: ElementProps) =>
  invalid
    ? shadow
      ? css`
          box-shadow: ${({ theme }) => theme.shadow.espansa},
            ${errorShadow}!important;
        `
      : css`
          box-shadow: ${errorShadow}!important;
          border: 0;
        `
    : null

const Element = styled.input<ElementProps>`
  appearance: none;
  width: 100%;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;

  height: ${({ inputSize, type }) =>
    type === 'textarea' ? 'auto' : inputSize === 'large' ? '50px' : '40px'};
  min-height: ${({ type }) => (type === 'textarea' ? '130px' : undefined)};

  padding: ${({ type }) => (type === 'textarea' ? '.5rem 1rem' : '0 1rem')};
  ${({ hasPrefix }) =>
    hasPrefix
      ? css`
          padding-left: calc(${inputPrefixSize} + 0.5rem);
        `
      : null};

  ${({ hasSideButton }) =>
    hasSideButton
      ? css`
          padding-right: calc(${inputSideButtonSize} + 0.5rem);
        `
      : null};

  background: ${({ bgColor }) => bgColor || 'white'};
  color: ${themeEstra.color.primari.neroEstra};

  border-radius: ${({ rounded }) => (rounded ? '20px' : '0')};
  outline: none;
  transition: box-shadow 0.3s;
  resize: ${({ type }) => (type === 'textarea' ? 'none' : undefined)};

  ${getShadowStyle}
  ${getErrorStyle}
`

interface PrefixProps {
  rounded?: boolean
}
const Prefix = styled.div<PrefixProps>`
  position: absolute;
  top: 0px;
  left: 0px;
  bottom: 0px;
  border-top-left-radius: ${({ rounded }) => (rounded ? '20px' : '0')};
  border-bottom-left-radius: ${({ rounded }) => (rounded ? '20px' : '0')};
  background-color: ${({ theme }) => theme.color.neutri.grigioBg};
  color: ${themeEstra.color.primari.grigioEstra};
  display: flex;
  width: ${inputPrefixSize};
  align-items: center;
  text-align: center;
  justify-content: center;
  margin: 0rem;
`
